import { format, parseISO, isValid } from 'date-fns';

// ===============================================
// CURRENCY FORMATTING
// ===============================================

export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
  options: Intl.NumberFormatOptions = {}
): string => {
  // Sanitize and validate currency code
  let sanitizedCurrency = currency?.toString().trim().toUpperCase() || 'USD';
  
  // Handle common invalid currency codes
  if (sanitizedCurrency === '$' || sanitizedCurrency === 'DOLLAR') {
    sanitizedCurrency = 'USD';
  } else if (sanitizedCurrency === '€' || sanitizedCurrency === 'EURO') {
    sanitizedCurrency = 'EUR';
  } else if (sanitizedCurrency === '£' || sanitizedCurrency === 'POUND') {
    sanitizedCurrency = 'GBP';
  } else if (sanitizedCurrency.length === 0) {
    sanitizedCurrency = 'USD';
  }
  
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: sanitizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  try {
    return new Intl.NumberFormat('en-US', defaultOptions).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    // Fallback: use number formatting with currency symbol
    return `${sanitizedCurrency} ${amount.toFixed(2)}`;
  }
};

export const formatCurrencyCompact = (amount: number, currency: string = 'USD'): string => {
  const absAmount = Math.abs(amount);
  
  if (absAmount >= 1e9) {
    return formatCurrency(amount / 1e9, currency, { maximumFractionDigits: 1 }) + 'B';
  } else if (absAmount >= 1e6) {
    return formatCurrency(amount / 1e6, currency, { maximumFractionDigits: 1 }) + 'M';
  } else if (absAmount >= 1e3) {
    return formatCurrency(amount / 1e3, currency, { maximumFractionDigits: 1 }) + 'K';
  }
  
  return formatCurrency(amount, currency);
};

// ===============================================
// PERCENTAGE FORMATTING
// ===============================================

export const formatPercentage = (
  value: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  try {
    // Convert decimal to percentage (e.g., 0.05 -> 5%)
    const percentValue = value / 100;
    return new Intl.NumberFormat('en-US', defaultOptions).format(percentValue);
  } catch (error) {
    console.error('Error formatting percentage:', error);
    return `${value.toFixed(2)}%`;
  }
};

export const formatPercentageChange = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${formatPercentage(value)}`;
};

// ===============================================
// NUMBER FORMATTING
// ===============================================

export const formatNumber = (
  value: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  const defaultOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  };

  try {
    return new Intl.NumberFormat('en-US', defaultOptions).format(value);
  } catch (error) {
    console.error('Error formatting number:', error);
    return value.toString();
  }
};

export const formatNumberCompact = (value: number): string => {
  const absValue = Math.abs(value);
  
  if (absValue >= 1e9) {
    return formatNumber(value / 1e9, { maximumFractionDigits: 1 }) + 'B';
  } else if (absValue >= 1e6) {
    return formatNumber(value / 1e6, { maximumFractionDigits: 1 }) + 'M';
  } else if (absValue >= 1e3) {
    return formatNumber(value / 1e3, { maximumFractionDigits: 1 }) + 'K';
  }
  
  return formatNumber(value);
};

// ===============================================
// DATE FORMATTING
// ===============================================

export const formatDate = (
  date: string | Date,
  formatString: string = 'MMM dd, yyyy'
): string => {
  try {
    let dateObj: Date;
    
    if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else {
      dateObj = date;
    }
    
    if (!isValid(dateObj)) {
      throw new Error('Invalid date');
    }
    
    return format(dateObj, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'MMM dd, yyyy HH:mm');
};

export const formatTime = (date: string | Date): string => {
  return formatDate(date, 'HH:mm');
};

export const formatRelativeTime = (date: string | Date): string => {
  try {
    let dateObj: Date;
    
    if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else {
      dateObj = date;
    }
    
    if (!isValid(dateObj)) {
      throw new Error('Invalid date');
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateObj);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Unknown';
  }
};

// ===============================================
// TRADING SPECIFIC FORMATTING
// ===============================================

export const formatPrice = (price: number, decimals: number = 4): string => {
  return formatNumber(price, { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals 
  });
};

export const formatVolume = (volume: number): string => {
  return formatNumberCompact(volume);
};

// ===============================================
// CRYPTO SPECIFIC FORMATTING
// ===============================================

/**
 * Format cryptocurrency prices with appropriate precision
 * - Prices < $1: 6 decimal places (e.g., $0.260000)
 * - Prices >= $1: 4 decimal places (e.g., $23.5200)
 */
export const formatCryptoPrice = (price: number, currency: string = 'USD'): string => {
  if (price === 0) {
    return formatCurrency(0, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const decimals = Math.abs(price) < 1 ? 6 : 4;
  
  return formatCurrency(price, currency, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format cryptocurrency quantities with appropriate precision
 * - Always show up to 8 decimal places but remove trailing zeros
 */
export const formatCryptoQuantity = (quantity: number): string => {
  if (quantity === 0) {
    return '0.00000000';
  }

  return formatNumber(quantity, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  });
};

/**
 * Format cryptocurrency amounts (price * quantity) with high precision
 * - Always show at least 4 decimal places for accuracy
 */
export const formatCryptoAmount = (amount: number, currency: string = 'USD'): string => {
  return formatCurrency(amount, currency, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6
  });
};

export const formatMarketCap = (marketCap: number): string => {
  return formatCurrencyCompact(marketCap);
};

export const formatPnL = (pnl: number): string => {
  const formatted = formatCurrency(Math.abs(pnl));
  return pnl >= 0 ? `+${formatted}` : `-${formatted}`;
};

export const formatPnLPercentage = (percentage: number): string => {
  const sign = percentage >= 0 ? '+' : '';
  return `${sign}${formatPercentage(percentage)}`;
};

// ===============================================
// VALIDATION HELPERS
// ===============================================

export const isValidNumber = (value: any): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
};

export const isValidDate = (date: any): boolean => {
  if (typeof date === 'string') {
    return isValid(parseISO(date));
  } else if (date instanceof Date) {
    return isValid(date);
  }
  return false;
};

// ===============================================
// UTILITY FUNCTIONS
// ===============================================

export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const formatSymbol = (symbol: string): string => {
  return symbol.toUpperCase().replace('/', ' / ');
};

export const getChangeColor = (value: number): string => {
  if (value > 0) return 'text-success-600';
  if (value < 0) return 'text-danger-600';
  return 'text-gray-600';
};

export const getChangeIcon = (value: number): 'up' | 'down' | 'neutral' => {
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'neutral';
}; 